var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useMemo } from 'react';
import styled, { css, } from 'styled-components';
import { margin, compose } from 'styled-system';
import { Spinner } from '../../Spinner';
import { buttonVariants, stylesBySize, useAction } from '../shared';
import { IconWithText } from '../shared/IconWithText';
import { useLoading } from './hooks/useLoading';
import { LoadingBar } from './LoadingBar';
import { a11y } from './strings';
const styledButtonConfig = {
    /**
     * This is necessary because styled components will not pass
     * non-standard attributes to DOM elements if `styled` is wrapping
     * a native DOM element. However, if a custom component is wrapped
     * by `styled`, then all attributes/props will be passed. See
     * https://styled-components.com/docs/basics#passed-props
     */
    shouldForwardProp: (prop, defaultValidatorFn) => {
        return prop === 'allytmln' || defaultValidatorFn(prop);
    },
};
const StyledButton = styled.button.withConfig(styledButtonConfig) `
  position: relative;
  overflow: hidden;
  z-index: 0;
  ${compose(margin, buttonVariants)}

  width: ${({ width }) => width};
  transition: box-shadow 0.15s ease, background 0.15s ease;
  box-sizing: border-box;
  ${({ variant }) => {
    return (variant !== 'link' &&
        variant !== 'disabled' &&
        css `
        &:hover {
          box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0.16);
          box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.08),
            0 3px 1px -2px rgba(0, 0, 0, 0.08), 0 1px 5px 0 rgba(0, 0, 0, 0.16);
        }
      `);
}}

  ${(props) => props.variant !== 'link' && props.size ? stylesBySize[props.size] : css ``};
`;
const StyledContentWrapper = styled.span `
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ isLoading, isLinkVariant }) => isLoading && isLinkVariant ? '0.0' : '1.0'};

  z-index: 1;
`;
const StyledSpinnerWrapper = styled.span `
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  z-index: 1;
`;
const defaultProps = {
    type: 'button',
    loading: false,
    variant: 'primary',
    disableFullWidthSmDown: false,
    fullWidthMd: false,
    disabled: false,
    iconPosition: 'left',
};
const Button = React.forwardRef((props, ref) => {
    var _a;
    const { text, type, className, disableFullWidthSmDown, fullWidthMd, ariaLabel, withAllyTM = true, id, onClick, disabled, icon, loading, ariaLiveLoading = a11y.ariaLiveLoading, unsupportedContent } = props, others = __rest(props, ["text", "type", "className", "disableFullWidthSmDown", "fullWidthMd", "ariaLabel", "withAllyTM", "id", "onClick", "disabled", "icon", "loading", "ariaLiveLoading", "unsupportedContent"]);
    const { safeVariant, safeId, allyTM, derivedSize, width, iconFill, theme } = useAction('button', id, withAllyTM, disableFullWidthSmDown, fullWidthMd, props.variant, text, 'size' in props ? props.size : undefined);
    const loaderTheme = useMemo(() => {
        const sv = safeVariant;
        return sv === 'primary' || sv === 'login' || sv === 'destructive'
            ? 'light'
            : 'dark';
    }, [safeVariant]);
    const { isLoading, loadingPercentage } = useLoading(ariaLiveLoading, loading);
    return (React.createElement(StyledButton, Object.assign({ ref: ref, type: type, "aria-label": ariaLabel, className: className, width: width, id: safeId }, (props.variant !== 'link' ? { size: derivedSize } : {}), { onClick: onClick, disabled: isLoading || disabled }, allyTM, others),
        props.variant !== 'link' && isLoading && (React.createElement(LoadingBar, { "$variant": (_a = props.variant) !== null && _a !== void 0 ? _a : 'primary', "$loading": typeof loadingPercentage === 'number'
                ? loadingPercentage
                : isLoading })),
        React.createElement(StyledContentWrapper, { isLoading: isLoading, isLinkVariant: props.variant === 'link' },
            React.createElement(IconWithText, Object.assign({ icon: icon, iconFill: iconFill, theme: theme }, (props.variant !== 'link' ? { size: props.size } : {})), text || unsupportedContent)),
        props.variant === 'link' && isLoading && (React.createElement(StyledSpinnerWrapper, null,
            React.createElement(Spinner, { size: "small", themeColor: loaderTheme, disableAriaLiveAnnouncement: true })))));
});
Button.defaultProps = defaultProps;
export { Button };
//# sourceMappingURL=Button.js.map